import type { StageResult, TimeStep } from './types';
import type { SimulationPlaybackFrame } from './simulationPlayback';

export type EngineeringCurveMetricId =
  | 'bottomholePressure'
  | 'bottomholeProppantConcentration'
  | 'pumpRate'
  | 'cumulativeFluid'
  | 'cumulativeProppant'
  | 'halfLength'
  | 'height'
  | 'width';

export type EngineeringCurveRow = {
  timeS: number;
  localTimeS: number;
  stageNumber: number;
  clusterId?: string;
} & Record<EngineeringCurveMetricId, number | null>;

export type EngineeringCurveScope = {
  kind: 'WELL' | 'STAGE' | 'CLUSTER';
  stageNumber?: number;
  clusterId?: string;
};

export const ENGINEERING_CURVE_METRICS: Record<EngineeringCurveMetricId, {
  label: string;
  unit: string;
  color: string;
  definition: string;
}> = {
  bottomholePressure: {
    label: '井底压力', unit: 'MPa', color: '#C2533A',
    definition: '井筒在射孔簇入口处的求解压力；不是地面压力或实测压力。',
  },
  bottomholeProppantConcentration: {
    label: '井底支撑剂浓度', unit: 'kg/m³', color: '#1E8449',
    definition: '泵注程序在射孔进料位置的支撑剂质量/载液体积；未建模井筒运移滞后。',
  },
  pumpRate: {
    label: '施工排量', unit: 'm³/min', color: '#2874A6',
    definition: '真实泵注程序排量；单簇模式显示求解器分配至该簇的排量。',
  },
  cumulativeFluid: {
    label: '累计注入液量', unit: 'm³', color: '#0891B2',
    definition: '按已提交泵注时间步累计的载液体积。',
  },
  cumulativeProppant: {
    label: '累计加砂量', unit: 'kg', color: '#D4770C',
    definition: '按已提交泵注时间步累计的支撑剂质量。',
  },
  halfLength: {
    label: '裂缝半长', unit: 'm', color: '#6A4C93',
    definition: '井筒至裂缝翼尖的单翼长度；全井模式显示当前施工段最大值。',
  },
  height: {
    label: '裂缝高度', unit: 'm', color: '#BE185D',
    definition: '真实求解时间步的总水力裂缝高度。',
  },
  width: {
    label: '裂缝宽度', unit: 'mm', color: '#7C3AED',
    definition: '井筒入口处最大动态缝宽。',
  },
};

const finite = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const rowFromStep = (
  step: TimeStep,
  stageNumber: number,
  clusterId: string | undefined,
  timeOffsetS: number,
): EngineeringCurveRow => ({
  timeS: timeOffsetS + (finite(step.t_s, step.time_s, step.time) ?? 0),
  localTimeS: finite(step.t_s, step.time_s, step.time) ?? 0,
  stageNumber,
  clusterId,
  bottomholePressure: finite(
    step.bottomhole_pressure_mpa,
    step.wellbore_pressure_pa === undefined ? undefined : Number(step.wellbore_pressure_pa) / 1e6,
  ),
  bottomholeProppantConcentration: finite(
    step.bottomhole_proppant_concentration_kgm3,
    step.sand_conc_kgm3,
  ),
  pumpRate: finite(
    step.stage_flow_rate_m3min,
    step.flow_rate_m3s === undefined ? undefined : Number(step.flow_rate_m3s) * 60,
    step.flow_rate === undefined ? undefined : Number(step.flow_rate) * 60,
  ),
  cumulativeFluid: finite(step.stage_cumulative_fluid_m3, step.Vi_m3, step.injected_volume_m3),
  cumulativeProppant: finite(
    clusterId ? step.cluster_cumulative_proppant_kg : step.stage_cumulative_proppant_kg,
    step.cumulative_proppant_kg,
  ),
  halfLength: finite(step.half_length_m, step.length_m, step.L_m),
  height: finite(step.height_m, step.h_m),
  width: finite(step.width_mm, step.w0_mm),
});

const stageSteps = (stage: StageResult): TimeStep[] => stage.time_steps ?? [];

export const buildEngineeringCurveRows = (
  stages: StageResult[],
  scope: EngineeringCurveScope,
): EngineeringCurveRow[] => {
  // The backend persists results in physical treatment order (toe to heel).
  // Preserve that order so chart time matches the Viewer exactly.
  const ordered = [...stages];
  if (scope.kind === 'CLUSTER') {
    const stage = ordered.find((item) => item.stage_number === scope.stageNumber);
    const cluster = stage?.clusters?.find((item) => item.cluster_id === scope.clusterId);
    return (cluster?.time_steps ?? cluster?.frames ?? []).map((step) => rowFromStep(
      step, stage?.stage_number ?? Number(scope.stageNumber ?? 0), cluster?.cluster_id, 0,
    ));
  }
  if (scope.kind === 'STAGE') {
    const stage = ordered.find((item) => item.stage_number === scope.stageNumber);
    return stage ? stageSteps(stage).map((step) => rowFromStep(step, stage.stage_number, undefined, 0)) : [];
  }

  let timeOffsetS = 0;
  let fluidOffsetM3 = 0;
  let proppantOffsetKg = 0;
  const rows: EngineeringCurveRow[] = [];
  ordered.forEach((stage) => {
    const stageRows = stageSteps(stage).map((step) => rowFromStep(step, stage.stage_number, undefined, timeOffsetS));
    stageRows.forEach((row) => rows.push({
      ...row,
      cumulativeFluid: row.cumulativeFluid === null ? null : fluidOffsetM3 + row.cumulativeFluid,
      cumulativeProppant: row.cumulativeProppant === null ? null : proppantOffsetKg + row.cumulativeProppant,
    }));
    const last = stageRows[stageRows.length - 1];
    if (last) {
      timeOffsetS += last.localTimeS;
      fluidOffsetM3 += last.cumulativeFluid ?? 0;
      proppantOffsetKg += last.cumulativeProppant ?? 0;
    }
  });
  return rows;
};

export const buildLiveEngineeringCurveRows = (
  frames: SimulationPlaybackFrame[],
  scope: EngineeringCurveScope,
): EngineeringCurveRow[] => {
  const rawRows = frames.flatMap((frame) => {
  const candidates = frame.clusters.filter((cluster) => (
    scope.kind === 'WELL'
    || (Number(cluster.stageId) === scope.stageNumber
      && (scope.kind === 'STAGE' || cluster.clusterId === scope.clusterId))
  ));
  if (!candidates.length) return [];
  const activeStage = Number(frame.stageId ?? candidates[0].stageId);
  const scoped = scope.kind === 'WELL'
    ? candidates.filter((cluster) => Number(cluster.stageId) === activeStage)
    : candidates;
  if (!scoped.length) return [];
  const sum = (getter: (cluster: typeof scoped[number]) => number | undefined): number | null => {
    const values = scoped.map(getter).filter((value): value is number => Number.isFinite(value));
    return values.length ? values.reduce((total, value) => total + value, 0) : null;
  };
  const max = (getter: (cluster: typeof scoped[number]) => number | undefined): number | null => {
    const values = scoped.map(getter).filter((value): value is number => Number.isFinite(value));
    return values.length ? Math.max(...values) : null;
  };
  const first = scoped[0];
    return [{
    timeS: frame.timelineTimeS ?? frame.timeS,
    localTimeS: frame.timeS,
    stageNumber: activeStage,
    clusterId: scope.kind === 'CLUSTER' ? first.clusterId : undefined,
    bottomholePressure: max((cluster) => cluster.bottomholePressureMpa),
    bottomholeProppantConcentration: max((cluster) => cluster.bottomholeProppantConcentrationKgm3),
    pumpRate: scope.kind === 'CLUSTER'
      ? finite(first.flowRateM3s * 60)
      : max((cluster) => cluster.stageRateM3min) ?? sum((cluster) => cluster.flowRateM3s * 60),
    cumulativeFluid: scope.kind === 'CLUSTER'
      ? finite(first.cumulativeFluidM3)
      : max((cluster) => cluster.cumulativeFluidM3),
    cumulativeProppant: scope.kind === 'CLUSTER'
      ? finite(first.cumulativeProppantKg)
      : sum((cluster) => cluster.cumulativeProppantKg),
    halfLength: max((cluster) => cluster.lengthM),
    height: max((cluster) => cluster.heightM),
    width: max((cluster) => cluster.actualWidthMm),
    }];
  });
  if (scope.kind !== 'WELL') return rawRows;
  let activeStage: number | null = null;
  let completedFluidM3 = 0;
  let completedProppantKg = 0;
  let lastStageFluidM3 = 0;
  let lastStageProppantKg = 0;
  return rawRows.map((row) => {
    if (activeStage !== null && row.stageNumber !== activeStage) {
      completedFluidM3 += lastStageFluidM3;
      completedProppantKg += lastStageProppantKg;
      lastStageFluidM3 = 0;
      lastStageProppantKg = 0;
    }
    activeStage = row.stageNumber;
    if (row.cumulativeFluid !== null) lastStageFluidM3 = Math.max(lastStageFluidM3, row.cumulativeFluid);
    if (row.cumulativeProppant !== null) lastStageProppantKg = Math.max(lastStageProppantKg, row.cumulativeProppant);
    return {
      ...row,
      cumulativeFluid: row.cumulativeFluid === null ? null : completedFluidM3 + row.cumulativeFluid,
      cumulativeProppant: row.cumulativeProppant === null ? null : completedProppantKg + row.cumulativeProppant,
    };
  });
};

export const availableCurveMetrics = (rows: EngineeringCurveRow[]): EngineeringCurveMetricId[] => (
  Object.keys(ENGINEERING_CURVE_METRICS) as EngineeringCurveMetricId[]
).filter((metric) => rows.some((row) => row[metric] !== null));
